import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Order } from 'shared/types/cart/Order';
import { useCart } from 'frontastic';

export interface OrderFetchOptions {
  limit?: number;
  cursor?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string | string[];
  deliveryMethod?: string;
  date?: string;
  requestFailedMsg?: string;
}

/**
 * Uses the actual return type of orderHistoryFilters from useCart.
 *
 * This keeps the hook aligned with the API contract and avoids maintaining
 * a separate duplicate response type.
 */
type OrderHistoryFiltersResponse = Awaited<ReturnType<ReturnType<typeof useCart>['orderHistoryFilters']>>;

/**
 * Keeps track of currently running order history requests.
 *
 * If the same request is triggered again before the first one finishes,
 * the existing promise is reused instead of making a duplicate API call.
 */
const inFlightOrderRequests = new Map<string, Promise<OrderHistoryFiltersResponse>>();

/**
 * Creates a stable string from a value.
 *
 * This is used to build a reliable request key for deduplication.
 * Object keys are sorted so payloads with the same values produce the same key,
 * even if their key order is different.
 */
const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => `${JSON.stringify(key)}:${stableStringify(val)}`)
      .join(',')}}`;
  }

  return JSON.stringify(value);
};

const useOrderFetchFilter = (initialOptions: OrderFetchOptions) => {
  const { orderHistoryFilters } = useCart();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState<number>(0);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [options, setOptions] = useState<OrderFetchOptions>(initialOptions);
  const [lastFetchHadError, setLastFetchHadError] = useState(false);

  /**
   * Stores the latest orderHistoryFilters function.
   *
   * This avoids stale closures inside the fetch effect without adding
   * orderHistoryFilters as a dependency and unintentionally re-running the effect.
   */
  const orderHistoryFiltersRef = useRef(orderHistoryFilters);

  /**
   * Tracks the latest request.
   *
   * This prevents older/slower responses from updating state after
   * a newer request has already started.
   */
  const requestIdRef = useRef(0);

  /**
   * Refs keep the latest values available inside callbacks.
   *
   * This avoids stale values in applyFilters and goToNextPage without
   * recreating those callbacks on every state update.
   */
  const optionsRef = useRef(options);
  const loadingRef = useRef(loading);
  const nextCursorRef = useRef(nextCursor);
  const lastFetchHadErrorRef = useRef(lastFetchHadError);

  useEffect(() => {
    orderHistoryFiltersRef.current = orderHistoryFilters;
  }, [orderHistoryFilters]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  useEffect(() => {
    lastFetchHadErrorRef.current = lastFetchHadError;
  }, [lastFetchHadError]);

  /**
   * Fetch orders whenever request options change.
   *
   * Handles:
   * - initial load
   * - filter changes
   * - pagination
   * - duplicate request prevention
   * - stale response protection
   */
  useEffect(() => {
    let isActive = true;
    const requestId = ++requestIdRef.current;

    /**
     * Build the API payload from the current hook options.
     *
     * UI-facing option names are mapped to API-facing names here:
     * - status -> orderState
     * - deliveryMethod -> fulfillmentType
     */
    const payload = {
      limit: options.limit ?? 5,
      cursor: options.cursor,
      dateFrom: options.dateFrom,
      dateTo: options.dateTo,
      orderState: options.status,
      fulfillmentType: options.deliveryMethod,
      date: options.date,
    };

    /**
     * Unique key for this request.
     *
     * If another request with the same payload is already running,
     * we reuse that promise.
     */
    const requestKey = stableStringify(payload);

    const run = async () => {
      setLoading(true);

      try {
        let requestPromise = inFlightOrderRequests.get(requestKey);

        /**
         * Start a new request only if the same request is not already in progress.
         */
        if (!requestPromise) {
          requestPromise = orderHistoryFiltersRef.current(payload).finally(() => {
            /**
             * Remove the request once it completes.
             *
             * This allows the same payload to be requested again later.
             */
            inFlightOrderRequests.delete(requestKey);
          });

          inFlightOrderRequests.set(requestKey, requestPromise);
        }

        const result = await requestPromise;

        /**
         * Ignore the response if:
         * - this effect is no longer active, or
         * - a newer request has already started.
         */
        if (!isActive || requestId !== requestIdRef.current) {
          return;
        }

        /**
         * Handle API-level errors.
         */
        if (result.isError) {
          toast.error(options.requestFailedMsg || 'We’re having trouble loading your orders. Try again.');
          setLastFetchHadError(true);
          return;
        }

        setLastFetchHadError(false);

        /**
         * Append orders when loading more using a cursor.
         * Replace orders when fetching the first page or applying filters.
         */
        setOrders((prev) => (options.cursor ? [...prev, ...(result.items || [])] : result.items || []));

        setTotal(result.total ?? 0);
        setNextCursor(result.nextCursor);
      } catch (error) {
        /**
         * Ignore errors from inactive or outdated requests.
         */
        if (!isActive || requestId !== requestIdRef.current) {
          return;
        }

        console.error('Order fetch failed:', error);

        /**
         * Reset order state when the request fails unexpectedly.
         */
        setOrders([]);
        setTotal(0);
        setNextCursor(undefined);
        setLastFetchHadError(true);

        toast.error(options.requestFailedMsg || 'We’re having trouble loading your orders. Try again.');
      } finally {
        /**
         * Only the latest active request should update loading state.
         */
        if (isActive && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void run();

    /**
     * Marks this request as inactive during cleanup.
     *
     * This prevents state updates after unmount or after options change.
     */
    return () => {
      isActive = false;
    };
  }, [options]);

  /**
   * Apply new filters and reset pagination.
   *
   * Existing orders are cleared immediately so the UI can show
   * a fresh loading state while new results are fetched.
   */
  const applyFilters = useCallback((newOptions: Partial<OrderFetchOptions>) => {
    const prev = optionsRef.current;

    const next: OrderFetchOptions = {
      ...prev,
      ...newOptions,
      cursor: undefined,
    };

    /**
     * Avoid triggering a fetch if the options did not actually change.
     */
    if (stableStringify(prev) === stableStringify(next)) {
      return;
    }

    optionsRef.current = next;

    setOrders([]);
    setNextCursor(undefined);
    setLoading(true);
    setOptions(next);
  }, []);

  /**
   * Load the next page of orders.
   *
   * Does nothing if:
   * - there is no next cursor
   * - a request is already in progress
   */
  const goToNextPage = useCallback(() => {
    const currentNextCursor = nextCursorRef.current;

    if (!currentNextCursor || loadingRef.current) {
      return;
    }

    const prev = optionsRef.current;

    const next: OrderFetchOptions = {
      ...prev,
      cursor: currentNextCursor,

      /**
       * If the last request failed, retry with the same limit.
       * Otherwise, increase the limit based on the initial page size.
       */
      limit: lastFetchHadErrorRef.current ? prev.limit : (prev.limit || 5) + (initialOptions.limit ?? 5),
    };

    /**
     * Avoid duplicate fetches if the calculated options are unchanged.
     */
    if (stableStringify(prev) === stableStringify(next)) {
      return;
    }

    optionsRef.current = next;

    setLoading(true);
    setOptions(next);
  }, [initialOptions.limit]);

  return {
    orders,
    loading,
    total,
    hasNext: Boolean(nextCursor),
    goToNextPage,
    applyFilters,
    currentOptions: options,
  };
};

export default useOrderFetchFilter;
